// components/ui/IntroUI.jsx
import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import IntroBackground from "../../../assets/svg/IntroBackground";
import IntroTitle from "../../../assets/svg/IntroTitle";
import useFlowStore from "../../store/flowStore";
import CameraManager from "../../manager/CameraManager";
import useCursorStore from "../../store/cursorStore";
import { preloadSecondaryAssets } from "../../../hooks/preloadSecondaryAssets";
import { audios, subtitles } from "../../../assets/assets";

export default function IntroUI({ onStartMusic }) {
  const buttonRef = useRef(null);
  const floatingCloudsTween = useRef(null); // 👈 pour garder une référence
  const { nextStep, setDesktopIsClickable, playAudioFile, setShowDesktopInteractUI } = useFlowStore.getState();
  const { setCursorScale, setCursorInteractive, setCursorTheme } =
    useCursorStore.getState();

  useEffect(() => {
    if (CameraManager.isReady) {
      const btn = buttonRef.current;
      const clouds = gsap.utils.toArray(".introBackgroundSVG path");
      setCursorInteractive(true);
      // 🌥️ Animation de flottement initiale
      floatingCloudsTween.current = gsap.to(clouds, {
        y: "+=10",
        duration: 2,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });

      const handleClick = () => {
        if (typeof onStartMusic === "function") onStartMusic();

        const audio = new Audio(audios.clickBuzzer);
        audio.play().catch(console.warn);

        preloadSecondaryAssets();

        // 🛑 Stop floating animation
        floatingCloudsTween.current?.kill();

        // Transition UI + Audio
        const tl = gsap.timeline();

        gsap.to(".introUI__title", {
          autoAlpha: 0,
          ease: "power2.inOut",
          duration: 0.4,
        });

        gsap.to(".introUI__button", {
          autoAlpha: 0,
          ease: "power2.inOut",
          duration: 0.4,
          delay: 0.2,
          onComplete: () => {            // ⏯️ Lance l'audio avant de continuer
            playAudioFile(audios.introduction, subtitles.introduction, () => {
              setDesktopIsClickable(true);
              setShowDesktopInteractUI(true);
              console.log("Audio intro fini");
            });

            setTimeout(() => {
               // 🌬️ Disparition animée des nuages
               tl.to(
                clouds,
                {
                  autoAlpha: 0,
                  y: -20,
                  scaleX: () => gsap.utils.random(0.8, 1.2),
                  scaleY: () => gsap.utils.random(0.8, 1.1),
                  skewX: () => gsap.utils.random(-3, 3),
                  skewY: () => gsap.utils.random(-5, 5),
                  transformOrigin: "50% 50%",
                  duration: 0.2,
                  ease: "power2.in",
                  stagger: 0.04,
                },
                "<"
              );

              // 👋 Disparition totale de l'UI
              tl.to(
                ".introUI",
                {
                  autoAlpha: 0,
                  duration: 0.5,
                  onComplete: () => {
                    setCursorTheme("light");
                    nextStep(() => {
                      console.log("Nuago to hub large angle");
                    });
                  },
                },
                "+=0.4"
              );
            }, 4000);
          },
        });
      };

      btn.addEventListener("click", handleClick);
      return () => {
        btn.removeEventListener("click", handleClick);
        floatingCloudsTween.current?.kill(); // nettoie à l’unmount
      };
    }
  }, [onStartMusic]);

  return (
    <div className="introUI">
      <IntroBackground />
      <IntroTitle className="introUI__title" />
      <div className="introUI__button">
        <button
          ref={buttonRef}
          type="button"
          onPointerEnter={() => setCursorScale(3.5)}
          onPointerLeave={() => setCursorScale(1)}
        >
          <svg
            width="191"
            height="49"
            viewBox="0 0 191 49"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M158.881 1.92937C170.945 5.45399 170.338 16.8609 175.846 21.6128C182.559 27.4046 197.941 33.9365 187.426 39.2061C176.792 44.535 149.537 43.2177 129.434 44.8708C114.163 46.1266 99.8892 47.5306 84.055 47.7065C64.2859 47.9261 40.4878 50.1509 26.4397 46.015C12.3978 41.881 24.249 34.681 20.2755 28.9292C15.9989 22.7384 -7.46201 16.813 2.3975 11.2124C12.283 5.59695 41.2311 7.18954 61.6999 5.39823C77.5009 4.01543 92.1236 1.33036 108.473 1.92937C125.164 2.54095 146.893 -1.57312 158.881 1.92937Z"
              fill="#3A67F1"
            />
          </svg>
          Débuter l’expérience
        </button>
      </div>
    </div>
  );
}
